import React, { createContext, ReactElement, useContext, useState } from 'react'
// Commons imports.
import Modal from '../Components/Modal'
import InlinePrompt, { InlinePromptProps } from '../Components/InlinePrompt/InlinePrompt'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import LanguageContext from './LanguageContext'
import { LanguagePrompt } from '../../../../i18n'

export type PromptAdditionalContent = {
    checkMessage?: string
    defaultChecked?: boolean
    infoMode?: boolean
}

/**
 * Prompt context type.
 */
export type PromptDisplayType = {
    displayPrompt: (
        message: string | ReactElement,
        confirmText: string,
        cancelText: string,
        confirmCallback?: () => void | Promise<void>,
        cancelCallback?: () => void,
        defaultChecked?: boolean,
        checkMessage?: string
    ) => void
    displayPromptPromise: (
        message: string | ReactElement,
        confirmLabel?: string,
        cancelLabel?: string,
        promptAdditionalContent?: PromptAdditionalContent,
        disableCancel?: boolean
    ) => Promise<Definable<boolean>>
}

/**
 * Component properties type.
 */
type Props = {
    isRouterMode?: boolean
    children: React.ReactNode
}

/**
 * Provides access to prompt resource.
 * @param children Children to render.
 * @param isRouterMode Flag to determine if the prompt is in router mode.
 */
export const PromptContextProvider = ({ isRouterMode, children }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    language.loadBundle(LanguagePrompt.NAME)

    const [promptSettings, setPromptSettings] = useState<InlinePromptProps>()
    const [isDisplayed, setIsDisplayed] = useState(false)

    /**
     * A method that is called on the confirmation of the prompt.
     */
    const onConfirmPromptClose = (onConfirmFunc: (() => void) | undefined): void => {
        if (onConfirmFunc) {
            onConfirmFunc()
        }
        setIsDisplayed(false)
    }

    /**
     * A method that is called on the cancelling of the prompt.
     */
    const onCancelPromptClose = (onCancelFunc: (() => void) | undefined): void => {
        if (onCancelFunc) {
            onCancelFunc()
        }
        setIsDisplayed(false)
    }

    /**
     * Method that displays the requested prompt with input defined.
     *
     * @param message The prompt message that wants displaying.
     * @param confirmButtonText The text for the confirmation button.
     * @param cancelButtonText The text for the cancellation button.
     * @param onConfirm The callback function called on the confirming of the prompt.
     * @param onCancel The callback function called on the cancelling of the prompt.
     * @param defaultChecked - The default checked state of the checkbox.
     * @param checkMessage - The message to display next to the checkbox.
     * @param infoMode - Flag to determine if the prompt is in info mode.
     */
    const displayPrompt = (
        message: string | ReactElement,
        confirmButtonText: string,
        cancelButtonText: string,
        onConfirm?: (messageChecked?: boolean) => void,
        onCancel?: () => void,
        defaultChecked?: boolean,
        checkMessage?: string,
        infoMode?: boolean
    ): void => {
        setPromptSettings({
            message,
            onConfirm: (messageChecked?: boolean) => {
                onConfirmPromptClose((): void => onConfirm && onConfirm(messageChecked))
            },
            confirmButtonText,
            onCancel: onCancel
                ? () => {
                      onCancelPromptClose(onCancel)
                  }
                : undefined,
            cancelButtonText,
            defaultChecked,
            checkMessage,
            infoMode
        })

        // Display state.
        setIsDisplayed(true)
    }

    const displayPromptPromise = (
        message: string | ReactElement,
        confirmLabel?: string,
        cancelLabel?: string,
        promptAdditionalContent?: PromptAdditionalContent,
        disableCancel?: boolean
    ): Promise<Definable<boolean>> =>
        new Promise<Definable<boolean>>((resolve, reject): void => {
            displayPrompt(
                message,
                confirmLabel ?? language.get(LanguagePrompt.LanguagePrompt_PromptConfirm),
                cancelLabel ?? language.get(LanguagePrompt.LanguagePrompt_PromptCancel),
                (checked?: boolean) => resolve(checked),
                disableCancel ? undefined : (): void => reject(),
                promptAdditionalContent?.defaultChecked,
                promptAdditionalContent?.checkMessage,
                promptAdditionalContent?.infoMode
            )
        })

    return (
        <PromptContext.Provider value={{ displayPrompt, displayPromptPromise }}>
            {children}

            {/* Prompt modal */}
            <Modal
                classNames={isRouterMode ? 'inline-prompt-modal' : ''}
                isOpen={isDisplayed}
                onClose={(): void => {
                    setIsDisplayed(false)
                }}
                closeOnBackdropClick={false}
            >
                {isDisplayed ? (
                    <React.Fragment>
                        {!!promptSettings && <InlinePrompt {...promptSettings} isRouterMode={isRouterMode} />}
                    </React.Fragment>
                ) : (
                    <></>
                )}
            </Modal>
        </PromptContext.Provider>
    )
}

/**
 * Context for prompt resource.
 */
// eslint-disable-next-line
const PromptContext = createContext<PromptDisplayType>(undefined as any)
export default PromptContext
